import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LayoutContainer from 'components/Common/LayoutContainer'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import LeLiveHeading from './LeLiveHeading'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Image from 'components/Common/Image'
import LoyaltyProductLogo from 'loyalty/components/LoyaltyProductLogo'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const SectionWrapper = styled.section`
  background-color: #394c3f;
  overflow: hidden;
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(32)};

  ${mediaQueryUp.desktop} {
    position: relative;
    height: ${rem(490)};
  }
`

const ImageComposition = styled.div`
  display: grid;
  place-items: start;
  width: fit-content;

  > * {
    grid-area: 1 / 1;
  }

  ${mediaQueryUp.desktop} {
    display: contents;
  }
`

const BackgroundImageWrapper = styled.div`
  border-radius: ${rem(3)};
  overflow: hidden;
  width: ${rem(269)};
  height: ${rem(191)};

  ${mediaQueryUp.desktop} {
    position: absolute;
    left: ${rem(392)};
    top: 0;
    width: ${rem(691)};
    height: ${rem(490)};
    margin-left: 0;
    border-radius: ${rem(8)};
    z-index: 1;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const CreditCardWrapper = styled.div`
  margin-left: ${rem(165)};
  margin-top: ${rem(50)};
  border-radius: ${rem(6)};
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.15);
  width: ${rem(143)};
  height: ${rem(94)};

  ${mediaQueryUp.desktop} {
    position: absolute;
    left: ${rem(825)};
    top: ${rem(135)};
    width: ${rem(368)};
    height: ${rem(241)};
    margin: 0;
    border-radius: ${rem(16)};
    border: 2px solid black;
    z-index: 2;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const TextCard = styled.div`
  position: relative;
  background-color: white;
  border-radius: ${rem(8)};
  padding: ${rem(32)} ${rem(16)} ${rem(20)};
  box-shadow: 0 20px 29px rgba(0, 0, 0, 0.1);
  align-self: stretch;

  ${mediaQueryUp.desktop} {
    position: absolute;
    left: 0;
    top: ${rem(85)};
    z-index: 2;
    max-width: none;
    width: ${rem(500)};
    padding: ${rem(40)};
  }
`

const Badge = styled.div`
  position: absolute;
  top: ${rem(-20)};
  left: 50%;
  transform: translateX(-50%);
  background-color: #b7410e;
  border: 1px solid #f3f1ea;
  border-radius: 1000px;
  padding: ${rem(8)} ${rem(20)};
  white-space: nowrap;
`

const LogoWrapper = styled.div`
  color: #363a45;
  width: ${rem(156)};
`

function LeLiveCreditCard() {
  const intl = useIntl()

  return (
    <SectionWrapper id="credit-card">
      <Sheet padding="56 24" desktopPadding="64 0">
        <LayoutContainer>
          <ContentLayout>
            <ImageComposition>
              <BackgroundImageWrapper>
                <Image
                  id="t0ci0kwi5a463zdvnnns"
                  alt={intl.formatMessage({
                    id: 'Dqoox5K9BTyUBT_KbSsL',
                    defaultMessage: 'Person using phone with Luxury Escapes app',
                    description: 'Accessible alt text for the background lifestyle image in the credit card section of the LE Live page',
                  })}
                  width={691}
                  height={490}
                  dpr={2}
                  enhancedFormats
                  fit="center"
                />
              </BackgroundImageWrapper>
              <CreditCardWrapper>
                <Image
                  id="v3kkqraezarn7pkucd9n"
                  alt={intl.formatMessage({
                    id: 'h8hnMxDdZZznjdGAtRMc',
                    defaultMessage: 'Luxury Escapes Société credit card',
                    description: 'Accessible alt text for the Société credit card product image in the LE Live event page',
                  })}
                  width={368}
                  height={241}
                  dpr={2}
                  enhancedFormats
                  fit="center"
                />
              </CreditCardWrapper>
            </ImageComposition>

            <TextCard>
              <Badge>
                <BodyText variant="medium" colour="neutral-eight">
                  {intl.formatMessage({
                    id: 'eNCLXzUkxmFxEZbqfZHl',
                    defaultMessage: 'Coming soon',
                    description: 'Status badge label indicating the credit card product is coming soon on the LE Live event page',
                  })}
                </BodyText>
              </Badge>

              <Group direction="vertical" gap={24} horizontalAlign="center">
                <Group direction="vertical" gap={8} horizontalAlign="center">
                  <LogoWrapper>
                    <LoyaltyProductLogo width="100%" height="100%" />
                  </LogoWrapper>
                  <LeLiveHeading variant="heading1alt" as="h2" align="center">
                    {intl.formatMessage({
                      id: '94yXzvrBCGlywbKT2gjz',
                      defaultMessage: 'Luxury Escapes Credit Card',
                      description: 'Main heading for the Luxury Escapes credit card promotional section on the LE Live event page',
                    })}
                  </LeLiveHeading>
                </Group>

                <Group direction="vertical" gap={4} horizontalAlign="center">
                  <HeadingTextBlock variant="heading5" align="center">
                    {intl.formatMessage({
                      id: 'xqee8sqqYUHzTYkJLQF2',
                      defaultMessage: 'It all points to your next holiday',
                      description: 'Subheading tagline for the credit card promotional section on the LE Live event page',
                    })}
                  </HeadingTextBlock>
                  <BodyTextBlock variant="large" align="center">
                    {intl.formatMessage({
                      id: 'futgo1k_vIl45ISAiocR',
                      defaultMessage: 'Earn Société points on everyday spending and turn groceries, dining and bills into your next escape.',
                      description: 'Body text describing Société credit card rewards and points earning benefits on the LE Live event page',
                    })}
                  </BodyTextBlock>
                </Group>
              </Group>
            </TextCard>
          </ContentLayout>
        </LayoutContainer>
      </Sheet>
    </SectionWrapper>
  )
}

export default LeLiveCreditCard
